import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { ChevronDown, LogOut, Menu, X, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NAV } from "./navConfig";
import { Button } from "@/components/ui/button";
import { currencySymbol, api } from "@/lib/api";
import { ChangePasswordDialog } from "./ChangePasswordDialog";

function NavGroup({ group, onNavigate, badges }) {
  const { can } = useAuth();
  const [open, setOpen] = useState(true);
  const items = group.items.filter((i) => can(i.module));
  if (!items.length) return null;
  const slug = group.title.toLowerCase().replace(/[^a-z]+/g, "-");
  return (
    <div className="mb-3">
      <button
        data-testid={`nav-group-${slug}`}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-white/40 hover:text-white/70 transition-colors"
      >
        {group.title}
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && (
        <div className="mt-1 space-y-0.5">
          {items.map((i) => (
            <NavLink
              key={i.path}
              to={i.path}
              end={i.path === "/"}
              onClick={onNavigate}
              data-testid={`nav-link-${i.module}`}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2 rounded-md text-[13px] transition-colors ${
                  isActive ? "bg-primary text-white font-semibold" : "text-white/70 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <i.icon className="h-4 w-4 shrink-0" />
              <span className="truncate flex-1">{i.label}</span>
              {badges[i.module] > 0 && (
                <span className="min-w-5 h-5 px-1.5 rounded-full bg-amber-400 text-[10px] font-bold text-black flex items-center justify-center" data-testid={`nav-badge-${i.module}`}>{badges[i.module]}</span>
              )}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function Sidebar({ onNavigate }) {
  const { can } = useAuth();
  const { pathname } = useLocation();
  const [badges, setBadges] = useState({});
  const voir = can("charges", "voir");
  const voirStock = can("stocks", "voir");
  useEffect(() => {
    if (voir) api.get("/charges/echeances").then((r) => setBadges((b) => ({ ...b, charges: r.data.items.length }))).catch(() => {});
    if (voirStock) api.get("/stocks/alertes").then((r) => setBadges((b) => ({ ...b, stocks: r.data.length }))).catch(() => {});
  }, [voir, voirStock, pathname]);
  return (
    <div className="h-full flex flex-col bg-[hsl(var(--sidebar))] kgd-grain">
      <div className="p-4">
        <div className="bg-white rounded-md p-2 flex items-center justify-center">
          <img src="/logo-kgd.jpeg" alt="KORIS Gaming Distribution" className="h-14 object-contain" data-testid="sidebar-logo" />
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-2 pb-6">
        {NAV.map((g) => (
          <NavGroup key={g.title} group={g} onNavigate={onNavigate} badges={badges} />
        ))}
      </nav>
      <div className="px-4 py-3 border-t border-white/10 text-[10px] text-white/30 tracking-wider">KGD · Gestion opérationnelle</div>
    </div>
  );
}

function Topbar({ onMenu }) {
  const { user, logout } = useAuth();
  const [pwd, setPwd] = useState(false);
  return (
    <header className="sticky top-0 z-30 backdrop-blur-md bg-background/85 border-b px-4 sm:px-6 h-14 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <button className="lg:hidden p-2 -ml-2" onClick={onMenu} data-testid="mobile-menu-button">
          <Menu className="h-5 w-5" />
        </button>
        <span className="hidden sm:inline-flex items-center gap-2 text-xs text-muted-foreground">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Données en temps réel
        </span>
      </div>
      <div className="flex items-center gap-2 sm:gap-4">
        <span className="text-[11px] font-mono px-2 py-1 rounded bg-accent text-accent-foreground" data-testid="currency-indicator">
          {currencySymbol()}
        </span>
        <div className="text-right hidden sm:block">
          <div className="text-sm font-semibold leading-tight" data-testid="current-user-name">{user?.name}</div>
          <div className="text-[11px] text-muted-foreground">{user?.role_name}</div>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setPwd(true)} title="Changer le mot de passe" data-testid="change-password-button">
          <KeyRound className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" onClick={logout} title="Déconnexion" data-testid="logout-button">
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
      <ChangePasswordDialog open={pwd} onOpenChange={setPwd} />
    </header>
  );
}

export default function AppLayout() {
  const [mobile, setMobile] = useState(false);
  const loc = useLocation();
  return (
    <div className="min-h-screen flex">
      <aside className="hidden lg:block w-64 shrink-0 fixed inset-y-0 left-0 z-40">
        <Sidebar />
      </aside>
      {mobile && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-72 h-full relative">
            <Sidebar onNavigate={() => setMobile(false)} />
            <button className="absolute top-3 right-3 text-white/70" onClick={() => setMobile(false)} data-testid="mobile-menu-close">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 bg-black/50" onClick={() => setMobile(false)} />
        </div>
      )}
      <div className="flex-1 lg:ml-64 min-w-0 flex flex-col">
        <Topbar onMenu={() => setMobile(true)} />
        <main key={loc.pathname} className="flex-1 p-4 sm:p-6 lg:p-8 fade-up">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
